import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  DataStatus,
  Dropdown,
  DropdownGroup,
  DropdownItem,
  Separator,
} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNotEmpty, isNotNil} from 'ramda';

import {
  INTERNAL_INSPECTION_TYPES,
  InspectionType,
  useCreateInspectionMutation,
  useGetInspectionTemplatesQuery,
} from '@dms/api/core';
import {useGetServiceCaseTypeQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

/** Keeps the spinner from collapsing the popover to nothing while the templates load. */
const TEMPLATES_LOADING_MIN_HEIGHT = 20;

interface CreateInspectionDropdownProps extends TestIdProps {
  vehicleId: string;
  serviceCaseTypeId: string;
  inspectionType: InspectionType.CHECKIN | InspectionType.HANDOVER;
  isDisabled?: boolean;
  isLoading?: boolean;
  onInspectionCreated: (inspectionId: string) => Promise<void>;
}

export function CreateInspectionDropdown(props: CreateInspectionDropdownProps) {
  const [hasOpened, setHasOpened] = useState(false);

  const {
    data: inspectionTemplates,
    isFetching: isFetchingTemplates,
    isUninitialized: isTemplatesUninitialized,
  } = useGetInspectionTemplatesQuery(
    {isActive: true},
    {
      skip: !hasOpened,
      selectFromResult: (queryResult) => ({
        ...queryResult,
        data: queryResult.data?.filter(
          (template) => !INTERNAL_INSPECTION_TYPES.includes(template.inspectionType)
        ),
      }),
    }
  );
  const {data: serviceCaseType} = useGetServiceCaseTypeQuery(
    isNotEmpty(props.serviceCaseTypeId) ? {serviceCaseTypeId: props.serviceCaseTypeId} : skipToken
  );
  const [createInspection, {isLoading: isCreating}] = useCreateInspectionMutation();

  const defaultTemplateId = match<
    CreateInspectionDropdownProps['inspectionType'],
    string | undefined
  >(props.inspectionType)
    .with(InspectionType.CHECKIN, () => serviceCaseType?.defaultCheckInInspectionId)
    .with(InspectionType.HANDOVER, () => serviceCaseType?.defaultHandoverInspectionId)
    .exhaustive();

  const defaultTemplate = isNotNil(defaultTemplateId)
    ? (inspectionTemplates ?? []).find((t) => t.id === defaultTemplateId)
    : undefined;

  const otherTemplates = isNotNil(defaultTemplate)
    ? (inspectionTemplates ?? []).filter((t) => t.id !== defaultTemplateId)
    : (inspectionTemplates ?? []);

  const handleSelect = (templateId: string) => {
    createInspection({
      vehicleId: props.vehicleId,
      createAuditRequestBody: {
        inspectionType: props.inspectionType,
        auditStructureId: templateId,
      },
    })
      .unwrap()
      .then(({id}) => props.onInspectionCreated(id))
      .catch(handleApiError);
  };

  return (
    // Hovering the control prefetches, so the menu is rarely empty on the first open.
    <div onMouseEnter={() => !props.isDisabled && setHasOpened(true)}>
      <Dropdown
        data-testid={props['data-testid']}
        onOpen={() => setHasOpened(true)}
        placement="bottom-end"
        isHeightLimited
        isDisabled={props.isDisabled || props.isLoading || isCreating}
        dropdownControl={
          <Button
            variant="link"
            leftIcon="content/add_circle"
            title={i18n.t('general.labels.createNew')}
            isDisabled={props.isDisabled}
            isLoading={props.isLoading || isCreating}
          />
        }
      >
        {/* Opening by keyboard or touch skips the hover prefetch, so the menu needs a spinner. */}
        <DataStatus
          isLoading={isTemplatesUninitialized || isFetchingTemplates}
          minHeight={TEMPLATES_LOADING_MIN_HEIGHT}
        >
          <Show
            when={
              isNotNil(defaultTemplate) &&
              isNotEmpty(defaultTemplate?.name) &&
              isNotEmpty(defaultTemplate?.id)
            }
          >
            <DropdownGroup label={i18n.t('general.labels.default')}>
              <DropdownItem
                data-testid={suffixTestId('defaultTemplate', props)}
                label={defaultTemplate?.name ?? ''}
                onClick={() => handleSelect(defaultTemplate?.id ?? '')}
              />
            </DropdownGroup>
            <Separator spacing={1} />
          </Show>
          {otherTemplates.map((t, index) => (
            <DropdownItem
              key={t.id}
              data-testid={suffixTestId(`template-[${index}]`, props)}
              label={t.name}
              onClick={() => handleSelect(t.id)}
            />
          ))}
        </DataStatus>
      </Dropdown>
    </div>
  );
}
